import React, { useState } from "react";
import type { PnlChartSectionType, TimeRangeFinite } from "types/types";
import { PnlCategoryLabelsEnum, ExtraPnlColumnsEnum } from "types/enums";
import { BREAKPOINT_TABLET, useMount } from "constants/constants";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { Collapse, useMediaQuery } from "@mui/material";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { sortAndFilterPnlBars } from "../pnlChartColumnsUtils";
import { PnlColumnType } from "../pnlChartColumnsTypes";
import { HeaderRowCell } from "./HeaderRowCell";
import { DataRowCell } from "./DataRowCell";
import { HOVER_UNDERLINE_CSS } from "components/Dashboard/range-selectors/steppable-range-selectors/ShiftSelector/ShiftSelectorStyles";
import styled from "styled-components/macro";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  CUSTOMERS_WHO_SHOW_FREQUENCY_SUM_IN_PNL_CHART,
  CUSTOM_PNL_CHART_HEADER_TITLES,
} from "constants/customers";
import { NUMBER_OF_EVENTS_KEY, TAG_NAMES_COLUMN } from "../columnDefinitions";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useIsTakingSnapshot } from "pages/SnapshotsPage/SnapshotElementButton";

/** each column has many header rows and many data (non-header) rows */
export function PnlColumn({
  pnlColumn,
  pnlSectionsDisplay,
  columnIdx,
  pnlChartWidth,
  isShowMoreExpanded,
  toggleShowMore,
  timeRange,
  isLeftmostColumnInChart = false,
  isRightmostColumnInChart = false,
  isRightmostColumnInPage = false,
  pnlChartKey,
  style = {},
  isLoading,
  disableAnimation,
}: {
  pnlSectionsDisplay: PnlChartSectionType[];
  pnlColumn: PnlColumnType;
  style?: Record<string, unknown>;
  columnIdx: number;
  pnlChartWidth: number;
  isShowMoreExpanded: boolean;
  toggleShowMore: () => void;
  timeRange: TimeRangeFinite | null;
  isLeftmostColumnInChart?: boolean;
  isRightmostColumnInChart?: boolean;
  isRightmostColumnInPage?: boolean;
  pnlChartKey: number;
  isLoading: boolean;
  disableAnimation;
}) {
  // Removed console log for production
  // collapse to animate in when loader skeleton switches to actual chart
  const isStorybook = useIsStorybook();
  const isPrint = useMediaQuery("print");
  const isFromSnapshotsPage =
    window.location.pathname.includes("fromSnapshots");
  // animates in (false -> true) if the animation is active
  const [isTakingSnapshot] = useIsTakingSnapshot();
  const disablePagination =
    isStorybook || isPrint || isFromSnapshotsPage || isTakingSnapshot;
  // Start mounted (don't animate) when disableAnimation is true or for special cases
  const [mounted, setMounted] = useState(disableAnimation || disablePagination);
  useMount(() => {
    if (!disableAnimation) {
      setMounted(true);
    }
  });

  const ckey = useCustomerKey();
  const { translate } = useFlightTranslation();
  const totalTime = pnlSectionsDisplay.reduce(
    (acc, cur) => acc + (cur.time ?? 0),
    0
  );
  // each section has one header and many data rows
  return (
    <PnlColumnStyles
      className={`${isLoading ? "loading " : ""}pnlColumn-${pnlColumn.dataKey}`}
      data-server-names={
        getIsOutputLossData(pnlColumn.dataKey)
          ? [SERVER_NAMES.midgardDataDumpsPnlLossesByUnit]
          : []
      }
      {...{
        $flexGrow: isPrint ? 0 : pnlColumn.flexGrow,
        $alignRight: pnlColumn.alignRight,
        style,
      }}
    >
      {/* section header e.g. Producing, Planned... */}
      {pnlSectionsDisplay.map((pnlSection: PnlChartSectionType, rowIdx) => {
        const {
          title: pnlTitle,
          /*  count, time, percent,  */
          bars, // bars => section rows (data)
        } = pnlSection;
        const headerDataDisplay = pnlSection[pnlColumn.dataKey];

        // show more / show less collapse/expand
        const { sortedBarsAlwaysVisible, sortedBarsInShowMore } =
          sortAndFilterPnlBars(bars);

        const dataRowCellProps = {
          timeRange,
          columnIdx,
          isLeftmostColumnInChart,
          pnlChartWidth,
          pnlTitle,
          pnlColumn,
          pnlChartKey,
        };

        // customize the HeaderRowCell title via CUSTOM_PNL_CHART_HEADER_TITLES
        const isTagNameColumn = pnlColumn.dataKey === TAG_NAMES_COLUMN.dataKey;
        const isFreqColumn = pnlColumn.dataKey === NUMBER_OF_EVENTS_KEY;
        const sumNumberOfEvents = bars.reduce(
          (acc, cur) => acc + (cur.numberOfEvents ?? 0),
          0
        );
        const customColumnHeaderTitle =
          isFreqColumn &&
          CUSTOMERS_WHO_SHOW_FREQUENCY_SUM_IN_PNL_CHART.includes(ckey)
            ? sumNumberOfEvents === 0
              ? `${translate("Freq")}.`
              : `${translate("Freq")}: ${sumNumberOfEvents}`
            : isTagNameColumn &&
              CUSTOM_PNL_CHART_HEADER_TITLES[ckey]?.[pnlTitle];
        const dataDisplay =
          customColumnHeaderTitle ||
          pnlColumn.columnHeaderTitle ||
          pnlColumn.formatFn({
            [pnlColumn.dataKey]: headerDataDisplay,
            pnlTitle: pnlTitle as PnlCategoryLabelsEnum,
          });
        return (
          <React.Fragment key={pnlTitle}>
            <HeaderRowCell
              {...{
                rowIdx,
                dataKey: pnlColumn.dataKey,
                title: pnlTitle,
                dataDisplay:
                  typeof dataDisplay === "string"
                    ? translate(dataDisplay)
                    : dataDisplay,
                flexGrow: pnlColumn.flexGrow,
                isLeftmostColumnInChart,
                isRightmostColumnInChart,
                totalTime,
              }}
            />
            <div className="dataRowsWrapper">
              {/* expand animation on mount */}
              <Collapse
                in={mounted}
                {...(disableAnimation ? { timeout: 0 } : {})}
              >
                {sortedBarsAlwaysVisible.map((bar, barRowIdx) => (
                  <DataRowCell
                    key={barRowIdx}
                    {...{
                      rowIdx: barRowIdx,
                      bar,
                      ...dataRowCellProps,
                    }}
                  />
                ))}
                {sortedBarsInShowMore.length > 0 &&
                  !(isPrint || isFromSnapshotsPage) && (
                    <>
                      {/* expand when click "show all..." */}
                      <Collapse
                        in={isShowMoreExpanded}
                        unmountOnExit={true}
                        {...(disableAnimation ? { timeout: 0 } : {})}
                      >
                        {sortedBarsInShowMore.map((bar, barRowIdx) => (
                          <DataRowCell
                            key={barRowIdx}
                            {...{
                              rowIdx:
                                barRowIdx + sortedBarsAlwaysVisible.length,
                              bar,
                              ...dataRowCellProps,
                            }}
                          />
                        ))}
                      </Collapse>
                      {isRightmostColumnInPage && (
                        <button
                          className="btnShowMore"
                          onClick={toggleShowMore}
                        >
                          {translate(
                            `show_${isShowMoreExpanded ? "some" : "all"}`
                          )}
                        </button>
                      )}
                    </>
                  )}
              </Collapse>
            </div>
          </React.Fragment>
        );
      })}
    </PnlColumnStyles>
  );
}

function getIsOutputLossData(dataKey: PnlColumnType["dataKey"]) {
  return [
    ExtraPnlColumnsEnum.outputLoss,
    ExtraPnlColumnsEnum.averageDurationMs,
    ExtraPnlColumnsEnum.numberOfEvents,
  ]
    .map(String)
    .includes(dataKey);
}

const PnlColumnStyles = styled.div<{
  $alignRight: boolean;
  $flexGrow?: {
    sm: number;
    md: number;
  };
}>`
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  position: relative;
  &,
  * {
    box-sizing: border-box;
  }
  * {
    ${props =>
      props.$alignRight
        ? `
    justify-content: flex-end
    `
        : ""}
  }
  ${props =>
    props.$flexGrow
      ? `
    flex-shrink: 0;
    flex-grow: ${props.$flexGrow.sm};
    @media (min-width: ${BREAKPOINT_TABLET}px) {
      flex-grow: ${props.$flexGrow.md};
    }
  `
      : ""}
  .zero {
    opacity: 0.2;
  }
  .hoursMinutes {
    display: flex;
    text-align: right;
  }
  .minutes {
    width: 40px;
    flex-grow: 0;
  }
  .dataRowsWrapper {
    position: relative;
    .btnShowMore {
      position: absolute;
      right: 0;
      bottom: -12px;
      white-space: nowrap;
      ${HOVER_UNDERLINE_CSS}
    }
  }
`;
